<template>
  <div class="zile-libere-module">
    <v-row class="module-row" dense>
      <!-- Left column: upcoming list -->
      <v-col cols="12" md="4" class="column list-column">
        <v-card outlined class="info-card">
          <v-card-title class="info-title d-flex align-center">
            <v-icon size="18" class="mr-2">mdi-calendar-blank-multiple</v-icon>
            {{ $t("nonWorkingDays.title") }}
            <v-spacer></v-spacer>
            <v-chip v-if="(upcomingOccurrences && upcomingOccurrences.length)" color="primary">
              {{ upcomingOccurrences.length }}
            </v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="list-wrapper">
            <div v-if="loading" class="text-center py-6">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else-if="!occurrences.length" class="empty-state">
              <v-icon size="28" color="grey">mdi-calendar-remove</v-icon>
              <p>{{ $t("nonWorkingDays.empty.noneInRange") }}</p>
            </div>
            <div v-else-if="!(upcomingOccurrences && upcomingOccurrences.length)" class="empty-state">
              <v-icon size="28" color="grey">mdi-calendar-check</v-icon>
              <p>{{ $t("nonWorkingDays.empty.noUpcoming") }}</p>
            </div>
            <v-slide-y-transition group tag="div" class="days-list" v-else>
              <div
                v-for="occurrence in limitedOccurrences"
                :key="`${occurrence.occurrence_date}-${occurrence.definitionId}`"
                class="day-entry"
              >
                <div class="day-badge" @click="handleCalendarSelection(occurrence.occurrence_date)">
                  <div class="day-number">{{ formatDayNumber(occurrence.occurrence_date) }}</div>
                  <div class="day-label">{{ formatWeekday(occurrence.occurrence_date) }}</div>
                </div>
                <div class="day-details">
                  <div class="day-title">
                    {{ occurrence.name }}
                    <v-chip
                      v-if="occurrence.is_recurring"
                      size="x-small"
                      color="primary"
                      class="ml-2"
                    >
                      {{ $t("nonWorkingDays.list.recurring") }}
                    </v-chip>
                  </div>
                  <div class="day-subtitle">
                    {{ formatFullDate(occurrence.occurrence_date) }}
                  </div>
                  <div class="day-meta" v-if="occurrence.company_id">
                    {{ $t("nonWorkingDays.list.company") }}: {{ occurrence.company_id }}
                  </div>
                </div>
                <div class="day-actions">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    @click="startEdit(occurrence.definitionId)"
                    :title="$t('app.actions.edit')"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    :loading="deletingId === occurrence.definitionId"
                    :title="$t('app.actions.delete')"
                    @click="confirmDelete(occurrence.definitionId)"
                  >
                    <v-icon size="18">mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-slide-y-transition>
            <div v-if="upcomingOccurrences && upcomingOccurrences.length > listLimit" class="see-more">
              <v-btn variant="text" size="small" @click="toggleList">
                {{ showAll ? $t("nonWorkingDays.list.showLess") : $t("nonWorkingDays.list.showMore") }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right column: calendar & form -->
      <v-col cols="12" md="8" class="column calendar-column">
        <v-card outlined class="info-card calendar-card">
          <v-card-text class="actions-bar">
            <div class="range-controls">
              <v-text-field
                v-model="periodStart"
                :label="$t('nonWorkingDays.filters.from')"
                type="date"
                hide-details
              ></v-text-field>
              <v-text-field
                v-model="periodEnd"
                :label="$t('nonWorkingDays.filters.to')"
                type="date"
                hide-details
              ></v-text-field>
              <v-text-field
                v-model="companyFilter"
                :label="$t('nonWorkingDays.filters.company')"
                type="number"
                hide-details
                :placeholder="$t('nonWorkingDays.filters.global')"
              ></v-text-field>
              <v-btn
                color="primary"
                variant="tonal"
                :disabled="!isRangeValid || loading"
                :loading="loading"
                @click="fetchDays"
              >
                {{ $t("nonWorkingDays.actions.reload") }}
              </v-btn>
            </div>
            <v-btn color="primary" @click="openCreateForm()">
              <v-icon size="18" start>mdi-plus</v-icon>
              {{ $t("nonWorkingDays.actions.addDay") }}
            </v-btn>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-text class="calendar-content">
            <v-alert
              v-if="rangeError"
              type="warning"
              density="comfortable"
              class="mb-4"
            >
              {{ rangeError }}
            </v-alert>
            <v-alert
              v-if="errorMessage"
              type="error"
              density="comfortable"
              class="mb-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-row class="calendar-form-row align-start" dense>
              <v-col :cols="showForm ? 12 : 12" :md="showForm ? 7 : 12" class="calendar-picker-col">
                <div class="calendar-wrapper">
                  <v-date-picker
                    v-model="calendarSelection"
                    color="primary"
                    :events="calendarEvents"
                    event-color="primary"
                    show-adjacent-months
                    @update:model-value="handleCalendarSelection"
                  ></v-date-picker>
                  <p class="calendar-hint">
                    {{ $t("nonWorkingDays.calendar.hint") }}
                  </p>
                </div>
              </v-col>

              <v-col v-if="showForm" cols="12" md="5" class="form-column">
                <v-card outlined class="info-card form-card">
                  <v-card-title class="info-title d-flex align-center">
                    <v-icon size="18" class="mr-2">{{ formState.id ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                    {{ formState.id ? $t("nonWorkingDays.form.editTitle") : $t("nonWorkingDays.form.addTitle") }}
                    <v-spacer></v-spacer>
                    <v-btn icon variant="text" @click="closeForm" :disabled="saving">
                      <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-form ref="formRef" @submit.prevent="submitForm">
                      <v-row dense>
                        <v-col cols="12" md="6">
                        <v-text-field
                          v-model="formState.date"
                          :label="$t('nonWorkingDays.form.date')"
                          type="date"
                          :rules="dateRules"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="formState.country_code"
                          :label="$t('nonWorkingDays.form.countryCode')"
                          readonly
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="formState.name"
                          :label="$t('nonWorkingDays.form.name')"
                          :rules="nameRules"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-switch
                          v-model="formState.is_recurring"
                          :label="$t('nonWorkingDays.form.recurring')"
                          inset
                        ></v-switch>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="formState.company_id"
                          :label="$t('nonWorkingDays.form.companyId')"
                          type="number"
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="formState.created_by_employee_id"
                          :label="$t('nonWorkingDays.form.createdBy')"
                          type="number"
                          hide-details="auto"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <div class="form-actions">
                      <v-btn variant="text" @click="closeForm" :disabled="saving">{{ $t("app.actions.cancel") }}</v-btn>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" :loading="saving" type="submit">
                        {{ formState.id ? $t("nonWorkingDays.form.saveChanges") : $t("nonWorkingDays.form.addDay") }}
                      </v-btn>
                    </div>
                  </v-form>
                </v-card-text>
              </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.show" :timeout="3000" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { formatDisplayDate, formatDisplayWeekday } from "@/Utile/displayFormat.mjs";

const toIsoDate = (value) => {
  if (!value) return "";
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return toIsoDate(parsed);
    }
  }
  return "";
};

const toDateObject = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (Array.isArray(value)) {
    if (!value.length) return null;
    return toDateObject(value[value.length - 1]);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const withTime = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]/);
    if (withTime) {
      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.getTime())) {
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      }
    }
    const dateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  return null;
};

const startOfYear = (date = new Date()) => {
  const d = new Date(date.getFullYear(), 0, 1);
  return toIsoDate(d);
};

const defaultFormState = () => ({
  id: null,
  date: "",
  name: "",
  is_recurring: false,
  company_id: "",
  country_code: "MD",
  created_by_employee_id: localStorage.getItem("employee-id") || "",
});

export default {
  name: "ZileLibere",
  setup() {
    const { t, locale } = useI18n();
    const definitions = ref([]);
    const occurrences = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const deletingId = ref(null);
    const errorMessage = ref("");
    const showForm = ref(false);
    const formState = reactive(defaultFormState());
    const calendarSelection = ref(null);
    const listLimit = 8;
    const showAll = ref(false);
    const snackbar = reactive({ show: false, color: "success", message: "" });
    const formRef = ref(null);

    const today = new Date();
    const periodStart = ref(startOfYear(today));
    const periodEnd = ref(toIsoDate(new Date(today.getFullYear() + 1, 11, 31)));
    const companyFilter = ref("");

    const dateRules = [
      (val) => !!val || t("nonWorkingDays.form.dateRequired"),
    ];
    const nameRules = [
      (val) => !!val || t("nonWorkingDays.form.nameRequired"),
      (val) => (val && val.length >= 3) || t("nonWorkingDays.form.nameMin"),
    ];

    const isRangeValid = computed(() => {
      if (!periodStart.value || !periodEnd.value) return false;
      return periodStart.value <= periodEnd.value;
    });

    const rangeError = computed(() => {
      if (!periodStart.value || !periodEnd.value) return t("nonWorkingDays.errors.rangeInvalid");
      if (periodStart.value > periodEnd.value) return t("nonWorkingDays.errors.rangeOrder");
      return "";
    });

    const definitionLookup = computed(() => {
      const map = new Map();
      definitions.value.forEach((item) => {
        map.set(Number(item.id), item);
      });
      return map;
    });

    const occurrenceLookup = computed(() => {
      const map = new Map();
      occurrences.value.forEach((occ) => {
        const key = occ.occurrence_date;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(occ);
      });
      return map;
    });

    const upcomingOccurrences = computed(() => {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return occurrences.value.filter((occ) => {
        const dateObj = toDateObject(occ.occurrence_date);
        if (!dateObj) return false;
        return dateObj >= todayStart;
      });
    });

    const sortedOccurrences = computed(() => {
      return [...upcomingOccurrences.value].sort((a, b) => {
        if (a.occurrence_date === b.occurrence_date) return a.name.localeCompare(b.name);
        return a.occurrence_date.localeCompare(b.occurrence_date);
      });
    });

    const limitedOccurrences = computed(() => {
      if (showAll.value) return sortedOccurrences.value;
      return sortedOccurrences.value.slice(0, listLimit);
    });

    const calendarEvents = computed(() => {
      return occurrences.value
        .map((occ) => {
          const dateObj = toDateObject(occ.occurrence_date);
          if (!dateObj) return null;
          return {
            date: dateObj,
            color: "primary",
          };
        })
        .filter(Boolean);
    });

    const toggleList = () => {
      showAll.value = !showAll.value;
    };

    const resetForm = () => {
      Object.assign(formState, defaultFormState());
    };

    const openCreateForm = (date = null) => {
      resetForm();
      const baseDate = toDateObject(date) || toDateObject(calendarSelection.value) || new Date();
      formState.date = toIsoDate(baseDate);
      formState.company_id = companyFilter.value !== "" ? String(companyFilter.value) : "";
      showForm.value = true;
      calendarSelection.value = baseDate;
    };

    const startEdit = (definitionId) => {
      const def = definitionLookup.value.get(Number(definitionId));
      if (!def) return;
      resetForm();
      formState.id = Number(def.id);
      formState.date = toIsoDate(def.date);
      formState.name = def.name || "";
      formState.is_recurring = Boolean(def.is_recurring);
      formState.country_code = def.country_code || "MD";
      formState.company_id = def.company_id !== null && def.company_id !== undefined ? String(def.company_id) : "";
      formState.created_by_employee_id = def.created_by_employee_id ? String(def.created_by_employee_id) : (localStorage.getItem("employee-id") || "");
      showForm.value = true;
      calendarSelection.value = toDateObject(def.date);
    };

    const closeForm = () => {
      if (saving.value) return;
      showForm.value = false;
      resetForm();
      calendarSelection.value = null;
    };

    const notify = (color, message) => {
      snackbar.color = color;
      snackbar.message = message;
      snackbar.show = true;
    };

    const fetchDays = async () => {
      if (!isRangeValid.value) return;
      loading.value = true;
      errorMessage.value = "";
      try {
        const filterTrimmed = String(companyFilter.value || "").trim();
        const companyParam = filterTrimmed === "" ? 0 : Number(filterTrimmed);
        if (Number.isNaN(companyParam)) {
          errorMessage.value = t("nonWorkingDays.errors.companyNumeric");
          loading.value = false;
          return;
        }
        const params = {
          company_id: companyParam,
          start_date: periodStart.value,
          end_date: periodEnd.value,
        };
        const { data } = await axios.get("/holidays/non-working-days", { params });
        definitions.value = Array.isArray(data?.definitions)
          ? data.definitions.map((def) => ({
              ...def,
              date: toIsoDate(def.date),
            }))
          : [];
        occurrences.value = Array.isArray(data?.occurrences)
          ? data.occurrences.map((occ) => ({
              ...occ,
              definitionId: Number(occ.id),
              occurrence_date: toIsoDate(occ.occurrence_date),
            }))
          : [];
      } catch (error) {
        console.error("fetchDays", error);
        errorMessage.value =
          error?.response?.data?.message || t("nonWorkingDays.errors.fetch");
      } finally {
        loading.value = false;
      }
    };

    const submitForm = async () => {
      if (!formState.date || !formState.name) return;
      const payload = {
        date: toIsoDate(formState.date),
        name: formState.name,
        is_recurring: formState.is_recurring ? 1 : 0,
        country_code: formState.country_code || "MD",
      };
      if (formState.company_id !== "") {
        const companyNumber = Number(formState.company_id);
        if (!Number.isNaN(companyNumber)) {
          payload.company_id = companyNumber;
        }
      }
      if (formState.created_by_employee_id) {
        const createdBy = Number(formState.created_by_employee_id);
        if (!Number.isNaN(createdBy)) {
          payload.created_by_employee_id = createdBy;
        }
      }

      saving.value = true;
      errorMessage.value = "";
      let shouldCloseForm = false;
      try {
        if (formState.id) {
          await axios.put(`/holidays/non-working-days/${formState.id}`, payload);
          notify("success", t("nonWorkingDays.messages.updated"));
        } else {
          await axios.post("/holidays/non-working-days", payload);
          notify("success", t("nonWorkingDays.messages.added"));
        }
        shouldCloseForm = true;
        await fetchDays();
      } catch (error) {
        console.error("submitForm", error);
        const message = error?.response?.data?.message || t("nonWorkingDays.errors.save");
        notify("error", message);
        errorMessage.value = message;
      } finally {
        saving.value = false;
        if (shouldCloseForm) {
          closeForm();
        }
      }
    };

    const confirmDelete = async (definitionId) => {
      const def = definitionLookup.value.get(Number(definitionId));
      if (!def) return;
      const confirmed = window.confirm(
        t("nonWorkingDays.confirmDelete", { name: def.name, date: toIsoDate(def.date) })
      );
      if (!confirmed) return;
      deletingId.value = Number(definitionId);
      try {
        await axios.delete(`/holidays/non-working-days/${definitionId}`);
        notify("success", t("nonWorkingDays.messages.deleted"));
        await fetchDays();
        if (formState.id === Number(definitionId)) {
          closeForm();
        }
      } catch (error) {
        console.error("confirmDelete", error);
        const message = error?.response?.data?.message || t("nonWorkingDays.errors.delete");
        notify("error", message);
      } finally {
        deletingId.value = null;
      }
    };

    const handleCalendarSelection = (value) => {
      const dateObj = toDateObject(value);
      const iso = toIsoDate(dateObj);
      if (!iso || !dateObj) return;
      calendarSelection.value = dateObj;
      const matches = occurrenceLookup.value.get(iso);
      if (matches && matches.length) {
        startEdit(matches[0].definitionId);
      } else {
        openCreateForm(dateObj);
      }
    };

    const formatDayNumber = (iso) => {
      if (!iso) return "";
      const date = new Date(`${iso}T00:00:00`);
      return String(date.getDate()).padStart(2, "0");
    };

    const formatWeekday = (iso) => {
      if (!iso) return "";
      const date = new Date(`${iso}T00:00:00`);
      return formatDisplayWeekday(date, locale.value, { style: "short" });
    };

    const formatFullDate = (iso) => {
      if (!iso) return "";
      const date = new Date(`${iso}T00:00:00`);
      return formatDisplayDate(date);
    };

    onMounted(() => {
      fetchDays();
    });

    watch(companyFilter, () => {
      fetchDays();
    });

    return {
      definitions,
      occurrences,
      loading,
      saving,
      deletingId,
      errorMessage,
      showForm,
      formState,
      calendarSelection,
      periodStart,
      periodEnd,
      listLimit,
      showAll,
      snackbar,
      formRef,
      dateRules,
      nameRules,
      isRangeValid,
      rangeError,
      limitedOccurrences,
      upcomingOccurrences,
      calendarEvents,
      formatDayNumber,
      formatWeekday,
      formatFullDate,
      toggleList,
      openCreateForm,
      startEdit,
      closeForm,
      submitForm,
      confirmDelete,
      handleCalendarSelection,
      fetchDays,
      companyFilter,
    };
  },
};
</script>

<style scoped>
.zile-libere-module {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.module-row {
  gap: 16px 0;
  align-items: flex-start;
}
.column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.list-column > .info-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.info-card {
  border-radius: var(--app-radius-base) !important;
  overflow: hidden;
}
.info-title {
  font-weight: 600;
  font-size: 16px;
}
.list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.50);
  gap: 8px;
  padding: 24px 0;
}
.days-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}
.calendar-column {
  flex: 1;
}
.calendar-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.calendar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.day-entry {
  display: grid;
  grid-template-columns: 72px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--app-radius-base);
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}
.day-entry:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}
.day-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: var(--app-radius-base);
  background: rgba(var(--v-theme-info), 0.12);
  color: rgb(var(--v-theme-info));
  cursor: pointer;
  transition: transform 0.2s ease;
}
.day-badge:hover {
  transform: scale(1.03);
}
.day-number {
  font-size: 22px;
  font-weight: 700;
}
.day-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.day-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.day-title {
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
}
.day-subtitle {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}
.day-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.50);
}
.day-actions {
  display: flex;
  gap: 4px;
}
.see-more {
  display: flex;
  justify-content: center;
  margin-top: 4px;
}
.actions-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.range-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.calendar-hint {
  margin-top: 12px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.50);
}
.calendar-form-row {
  align-items: flex-start;
  flex: 1;
}
.calendar-picker-col,
.form-column {
  display: flex;
  flex-direction: column;
}
.calendar-wrapper,
.form-column .info-card {
  width: 100%;
}
.form-column .info-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.form-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.form-card .v-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.form-card .v-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.form-card .form-actions {
  margin-top: auto;
}
.calendar-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.calendar-wrapper :deep(.v-date-picker) {
  flex: 1;
}
.form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
:deep(.v-date-picker__events) {
  display: none;
}
:deep(.v-date-picker-month__day--non-working .v-btn) {
  background-color: rgba(var(--v-theme-info), 0.18) !important;
  color: rgb(var(--v-theme-info)) !important;
}
</style>
